/**
 * Document vocabulary shared by the API (documents module) and the SPA (Kontaktakte document rows and
 * browser, Deal-Akte 07): enum values of the `document` table with their German labels and the badge
 * tone of the "Dokumentenzeile" (geprüft/vollständig/unterschrieben = success, fehlt/offen = warning,
 * gesendet/Entwurf = info, archiviert = neutral). The server sends label and tone with every row; the
 * client uses these maps only to render filters and the upload dialog.
 */
export const documentStatuses = [
    'draft',
    'uploaded',
    'reviewed',
    'missing',
    'requested',
    'archived',
];
export const documentStatusLabels = {
    draft: 'Entwurf',
    uploaded: 'hochgeladen',
    reviewed: 'geprüft',
    missing: 'fehlt',
    requested: 'offen',
    archived: 'archiviert',
};
export const documentStatusTones = {
    draft: 'info',
    uploaded: 'info',
    reviewed: 'success',
    missing: 'warning',
    requested: 'warning',
    archived: 'neutral',
};
export const documentParties = ['customer', 'broker', 'financier', 'developer', 'internal'];
/** Deal-Akte 07 columns and the browser's "Nach Partei" navigation (Kontaktakte I.06). */
export const documentPartyLabels = {
    customer: 'Kunde',
    broker: 'Vermittler',
    financier: 'Finanzierer',
    developer: 'Bauträger',
    internal: 'Intern',
};
export const documentCategories = [
    'proof_of_income',
    'identity',
    'property_documents',
    'financing',
    'contract',
    'internal',
    'other',
];
export const documentCategoryLabels = {
    proof_of_income: 'Einkommensnachweise',
    identity: 'Ausweis',
    property_documents: 'Objektunterlagen',
    financing: 'Finanzierung',
    contract: 'Verträge',
    internal: 'Intern',
    other: 'Sonstiges',
};
/** Groups of the document taxonomy (0032) — the headings of the type pickers, in display order. */
export const documentTypeGroups = [
    'customer',
    'financing',
    'sales',
    'property_legal',
    'property_technical',
    'property_economics',
    'property_management',
    'rental',
    // 0044 (PKV)
    'pkv',
];
export const documentTypeGroupLabels = {
    customer: 'Kundenunterlagen',
    financing: 'Finanzierung',
    sales: 'Vertrieb',
    property_legal: 'Objekt · Recht',
    property_technical: 'Objekt · Technik',
    property_economics: 'Objekt · Wirtschaft',
    property_management: 'Verwaltung / WEG',
    rental: 'Vermietung',
    pkv: 'PKV',
};
/** Records a document type applies to (0032): pickers offer the types whose scope contains the current record. */
export const documentTypeScopes = [
    'property',
    'unit',
    'tenancy',
    'contact',
    'deal',
    'financing',
    // 0044 (PKV)
    'risk_inquiry',
    'pkv_application',
    'health_questionnaire',
];
export const documentTypeScopeLabels = {
    property: 'Objekt',
    unit: 'Einheit',
    tenancy: 'Mietverhältnis',
    contact: 'Kontakt',
    deal: 'Deal',
    financing: 'Finanzierung',
    risk_inquiry: 'Risikovoranfrage',
    pkv_application: 'PKV-Antrag',
    health_questionnaire: 'Gesundheitsfragebogen',
};
/**
 * The types of one record grouped for a picker: only the types whose scope contains `scope` (all when null),
 * groups in `documentTypeGroups` order, names sorted alphabetically inside a group, empty groups left out.
 */
export function groupDocumentTypes(types, scope) {
    const visible = scope === null ? types : types.filter((type) => type.scope.includes(scope));
    return documentTypeGroups
        .map((group) => ({
        group,
        label: documentTypeGroupLabels[group],
        items: visible
            .filter((type) => type.group === group)
            .sort((a, b) => a.name.localeCompare(b.name, 'de')),
    }))
        .filter((entry) => entry.items.length > 0);
}
export const documentOrigins = [
    'upload',
    'portal',
    'developer_link',
    'email',
    'system',
    'ai',
    'import',
];
export const documentRequestChannels = ['email', 'whatsapp', 'portal'];
export const documentRequestStatuses = ['open', 'reminded', 'delivered', 'cancelled'];
export const documentRequestStatusLabels = {
    open: 'offen',
    reminded: 'erinnert',
    delivered: 'geliefert',
    cancelled: 'zurückgezogen',
};
/** MIME types accepted for customer documents (§9.4: PDF, JPG, PNG). */
export const customerDocumentMimeTypes = ['application/pdf', 'image/jpeg', 'image/png'];
/** The screens use an ordinary non-breaking space before the unit (see format.ts). */
const NBSP = '\u00a0';
const sizeWhole = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 });
const sizeOneDecimal = new Intl.NumberFormat('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
/** 319488 → "312 kB", 2516582 → "2,4 MB" — the sizes as the document rows of the screens show them. */
export function formatFileSize(bytes) {
    const kilobytes = bytes / 1024;
    if (kilobytes < 1000)
        return `${sizeWhole.format(Math.max(1, Math.round(kilobytes)))}${NBSP}kB`;
    return `${sizeOneDecimal.format(kilobytes / 1024)}${NBSP}MB`;
}
/** "PDF", "JPG", "PNG" (or the upper-cased subtype) for the meta line "PDF · 2,4 MB · 24.09.2026". */
export function fileKindLabel(mime) {
    switch (mime) {
        case 'application/pdf':
            return 'PDF';
        case 'image/jpeg':
            return 'JPG';
        case 'image/png':
            return 'PNG';
        default: {
            const subtype = mime.split('/')[1] ?? mime;
            return subtype.toUpperCase();
        }
    }
}
//# sourceMappingURL=documents.js.map